import * as React from "react"
import Layout from "../../components/layout"
import Seo from "../../components/seo"
import TitleWord from "../../components/titleWord"
import MoreSolutions from "../../components/moreSolutions"
import BookDemoLink from "../../components/BookDemoLink"
import { StaticImage } from "gatsby-plugin-image"

const LeadScoring = ({ location }) => {
  return (
    <Layout location={location} title="Lead Scoring">
      <div className="bg-white pt-20 tablet:pt-44">
        <div className="flex items-center justify-center gap-6 flex-wrap w-full">
          <TitleWord className="bg-yellow-200 tablet:px-28">
            Lead Scoring
          </TitleWord>
        </div>
        <p className="px-14 tablet:px-0 text-center text-base tablet:text-3xl mt-10 mx-auto max-w-3xl mb-8">
          Employ AI to find and prioritize leads with high conversion potential,
          and seamlessly integrate these predictions with your CRM to optimize
          conversion rates, time-to-close, and revenue growth.
        </p>

        {/* Main diagram */}
        <div className="w-full px-6 hidden tablet:block">
          <div className="container mx-auto py-32 px-10 flex items-center justify-center bg-purple-100 rounded-header">
            <StaticImage
              placeholder="blurred"
              formats={["auto", "webp", "avif"]}
              src={`../../images/illustrations/solution-lead-scoring.png`}
              alt="Lead scoring tech diagram"
            />
          </div>
        </div>

        {/* Article blocks */}
        <div className="px-6 tablet:px-0">
          <div className="tablet:pt-12 pb-6 container  mobile:grid grid-cols-2 grid-flow-row items-stretch gap-6 space-y-6 tablet:space-y-0">
            <div className="hidden tablet:block bg-green-300 p-10 w-full rounded-3xl">
              <h3 className="text-4xl leading-tight">
                Forget handcrafted rules, maximize conversion with AI
              </h3>
              <p className="mt-4 text-xl">
                Don’t rely on handcrafted rules and gut feelings for your modern
                sales engine. There’s a wealth of information hidden within your
                existing data. Machine learning allows you to fully harness this
                data, finding complex patterns and trends, that creates precise
                lead prioritization to maximize your conversion rates.
              </p>
            </div>
            <div className="pt-2 tablet:pt-0 rounded-header bg-green-300 tablet:bg-green-100 h-full flex flex-col tablet:flex-row items-center justify-center">
              <StaticImage
                width={361}
                placeholder="blurred"
                className="m-auto"
                formats={["auto", "webp", "avif"]}
                src={`../../images/illustrations/lead-scoring-1.png`}
                alt="Coworker AI example"
              />
              <h3 className="w-full max-w-96 mobile:text-center tablet:hidden text-2xl leading-tight mt-2 px-6 ">
                Forget handcrafted rules, maximize conversion with AI
              </h3>
              <p className="mobile:text-center mobile:max-w-full tablet:hidden text-base mt-3 px-6 pb-6 max-w-96">
                Don’t rely on handcrafted rules and gut feelings for your modern
                sales engine. There’s a wealth of information hidden within your
                existing data. Machine learning allows you to fully harness this
                data, finding complex patterns and trends, that creates precise
                lead prioritization to maximize your conversion rates.
              </p>
            </div>

            <div className="pt-2 tablet:pt-0 rounded-header tablet:bg-blue-100 h-full bg-blue-300 flex flex-col tablet:flex-row items-center justify-center">
              <StaticImage
                width={407}
                placeholder="blurred"
                className="m-auto"
                formats={["auto", "webp", "avif"]}
                src={`../../images/illustrations/lead-scoring-2.png`}
                alt="Coworker AI example"
              />
              <h3 className="w-full max-w-96 text-left tablet:hidden text-2xl leading-tight mt-4 px-6 ">
                Predictions in your CRM
              </h3>
              <p className="tablet:hidden text-base mt-6 px-6 pb-6 max-w-96">
                Integrate AI-powered predictions directly into your Customer
                Relationship Management (CRM) system. This enables your teams to
                focus on the most promising opportunities, reducing time and
                financial investments in less likely prospects. Access to
                real-time scoring empowers your sales and revenue teams to
                operate with unprecedented efficiency.
              </p>
            </div>
            <div className="hidden tablet:block bg-blue-200 p-10 w-full rounded-3xl">
              <h3 className="text-4xl leading-tight">
                Predictions in your CRM
              </h3>
              <p className="mt-4 text-xl">
                Integrate AI-powered predictions directly into your Customer
                Relationship Management (CRM) system. This enables your teams to
                focus on the most promising opportunities, reducing time and
                financial investments in less likely prospects. Access to
                real-time scoring empowers your sales and revenue teams to
                operate with unprecedented efficiency.
              </p>
            </div>
            <div className="hidden tablet:block bg-red-200 p-10 w-full rounded-3xl">
              <h3 className="text-4xl leading-tight">
                AI that’s always improving
              </h3>
              <p className="mt-8 text-xl">
                Each successful conversion provides valuable insights. Infer
                leverages this accumulating data to regularly update your
                models, to ensure that your sales tactics are always informed by
                the most current data. Adapt swiftly to market dynamics with an
                ever-evolving approach to lead scoring.
              </p>
            </div>
            <div className="rounded-header tablet:bg-red-100 pt-2 tablet:pt-0 h-full bg-red-200 flex flex-col tablet:flex-row items-center justify-center">
              <StaticImage
                width={265}
                placeholder="blurred"
                className="m-auto"
                formats={["auto", "webp", "avif"]}
                src={`../../images/illustrations/lead-scoring-3.png`}
                alt="Coworker AI example"
              />
              <h3 className="w-full max-w-96 text-left tablet:hidden text-2xl leading-tight mt-6 px-6 ">
                AI that’s always improving
              </h3>
              <p className="tablet:hidden text-base mt-6 px-6 pb-6 max-w-96">
                Each successful conversion provides valuable insights. Infer
                leverages this accumulating data to regularly update your
                models, to ensure that your sales tactics are always informed by
                the most current data. Adapt swiftly to market dynamics with an
                ever-evolving approach to lead scoring.
              </p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="w-full px-6 pt-6 mobile:pt-12 tablet:pt-20 pb-8 mb-10 tablet:mb-32">
          <div className="w-full px-10 tablet:px-0 py-10 border border-black rounded-header">
            <div className="container flex flex-col tablet:flex-row items-center justify-between">
              <h3 className="text-2xl leading-tight">
                Contact us to start your free trial or book a demo
              </h3>
              <div className="flex items-center flex-col mobile:flex-row mt-10 tablet:mt-0 space-y-5 mobile:space-y-0 mobile:space-x-4">
                <BookDemoLink className="btn-black w-56 h-12" />
              </div>
            </div>
          </div>
        </div>

        {/* More solutions */}
        <MoreSolutions currentSolution="Lead Scoring" tag="your-customer" />
      </div>
    </Layout>
  )
}

export default LeadScoring

export const Head = () => <Seo title="Lead scoring" />
